import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Select from 'react-select';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import useRefetchRecurringData from '../components/presentational/hooks/useRefetchRecurringData';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  convertRecurringItem,
  updateRecurringItemSchedule,
} from '../ducks/calendarActions';
import { showErrorToast } from '../../../shared/utils/toastManager';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const RemoveDivIconWrapper = styled('div')``;

const ClickHereDiv = styled('div')``;

const ModalTextTop = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
`;

const Subtext = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-top: 5px;
  color: #9E9E9E;
`;

const Subtext2 = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  margin-top: 15px;
  color: black;
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  margin-top: 30px;
`;

const TopTextAndX = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SchedulingOptionsStack = styled('div')`
  display: flex;
  flex-direction: column;
`;

const TopSelectRow = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;
`;

const DatePickerStack = styled('div')`
  display: flex;
  flex-direction: column;
`;

const DatePickerRow = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;
`;

const PickerAndText = styled('div')`
  display: flex;
  flex-direction: column;
  width: 49%;
`;

const NewCalendarButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  background: #444444;
  color: white;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

const ManageCalendarsButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  color: #444444;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

const DaySelectors = styled('div')`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const DaySelector = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#444444' : 'white')};
  color: ${(props) => (props.selected ? 'white' : '#444444')};
  border: 1px solid ${(props) => (props.selected ? '#444444' : '#E0E0E0')};
  transition: all 0.2s ease;

  &:hover {
    border-color: #444444;
  }
`;

const SelectDaysLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  margin-top: 15px;
  color: black;
`;

const SetRecurringItemModal = ({
  closeModal,
}) => {
  const refetchRecurringData = useRefetchRecurringData();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const currentMonth = moment(selectedDate).startOf('month').toDate();
  const nextMonth = moment(selectedDate).add(1, 'month').startOf('month').toDate();
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const calendarPickerFirstMonth = useSelector(
    (state) => state.calendar.data.firstMonthInPicker,
  );

  const [startDate, setStartDate] = useState(
    modalData?.schedule?.startDate ?? moment(selectedDate).format('YYYY-MM-DD'),
  );

  const [startDateString, setStartDateString] = useState(
    modalData?.schedule?.startDate ? moment(modalData.schedule.startDate).format('YYYY-MM-DD') : moment(selectedDate).format('YYYY-MM-DD'),
  );

  const [endDate, setEndDate] = useState(
    modalData?.schedule?.endDate ?? null,
  );

  const [endDateString, setEndDateString] = useState(
    modalData?.schedule?.endDate ? moment(modalData.schedule.endDate).format('YYYY-MM-DD') : '',
  );

  const initialSchedule = modalData?.schedule;

  const leftSelectOptions = [
    {
      name: 'Every Day',
      id: 0,
    },
    {
      name: 'Weekdays',
      id: 1,
    },
    {
      name: 'Days with Scheduled Workouts',
      id: 2,
    },
    {
      name: 'Custom',
      id: 3,
    },
  ];

  const rightSelectOptions = [
    {
      name: 'Before Workout',
      id: 0,
    },
    {
      name: 'After Workout',
      id: 1,
    },
  ];

  const [selectedCadence, setSelectedCadence] = useState(() => {
    if (!initialSchedule) return leftSelectOptions[0];
    return (
      leftSelectOptions.find((o) => o.id === initialSchedule.cadence)
    ?? leftSelectOptions[0]
    );
  });

  const [selectedPosition, setSelectedPosition] = useState(() => {
    if (!initialSchedule) return rightSelectOptions[0];
    return (
      rightSelectOptions.find((o) => o.id === initialSchedule.position)
    ?? rightSelectOptions[0]
    );
  });

  const [selectedDays, setSelectedDays] = useState(() => {
    if (
      !initialSchedule
    || initialSchedule.cadence !== 3
    || !initialSchedule.customDays
    ) {
      return {
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
      };
    }

    const base = {
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
    };

    const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    initialSchedule.customDays.forEach((i) => {
      if (map[i]) base[map[i]] = true;
    });

    return base;
  });

  const dispatch = useDispatch();

  // Date picker handlers that update both formats
  const handleStartDateChange = (date) => {
    const momentDate = moment(date);
    setStartDate(momentDate.toDate());
    setStartDateString(momentDate.format('YYYY-MM-DD'));

    // Clear end date if start date is after current end date
    if (endDate && momentDate.isAfter(moment(endDate))) {
      setEndDate(null);
      setEndDateString('');
    }
  };

  const handleEndDateChange = (date) => {
    /** user can clear date, end date is not required */
    if (!date) {
      setEndDate(null);
      setEndDateString(null);
      return;
    }

    const momentDate = moment(date);
    setEndDate(momentDate.toDate());
    setEndDateString(momentDate.format('YYYY-MM-DD'));
  };

  const formatDate = () => `${moment(selectedDate).format('YYYY-MM-DD')}`;
  const formatRangeDate = (rangeDate) => `${moment(rangeDate).format('YYYY-MM-DD')}`;

  // Helper function to toggle day selection
  const toggleDay = (day) => {
    setSelectedDays({
      ...selectedDays,
      [day]: !selectedDays[day],
    });
  };

  const handleSave = () => {
    // Validate required fields
    if (!startDate) {
      showErrorToast('Please select a start date');
      return;
    }
    if (!selectedCadence || (!selectedCadence.id && selectedCadence.id !== 0)) {
      showErrorToast('Please select a scheduling option');
      return;
    }
    if (!selectedPosition || (!selectedPosition.id && selectedPosition.id !== 0)) {
      showErrorToast('Please select a position option');
      return;
    }
    // Continue with the existing logic
    if (!modalData.preSave && !modalData.isRecurring) {
      const updateItem = {
        cadence: selectedCadence.id,
        position: selectedPosition.id,
        startDate: formatRangeDate(startDate),
        ...(endDate && { endDate: formatRangeDate(endDate) }),
      };

      // Add customDays if cadence is Custom (3)
      if (selectedCadence.id === 3) {
        const dayMap = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };

        updateItem.customDays = Object.entries(selectedDays)
          .filter(([, isSelected]) => isSelected)
          .map(([day]) => dayMap[day]);
      }
      /** when we convert a recurring item, onsuccess we need to
       * fetchCalendarPickerItems so that calendar dots update
       * we also need to re fetch recurring items so that we can get
       * the proper updates in redux. These updates for recurring are too
       * complex to try and update directly in state, so fethcing again is the
       * safest option
       */
      dispatch(convertRecurringItem(
        currentUser.accountCode,
        updateItem,
        modalData.assignedId,
        modalData.itemIndex,
        formatDate(),
        {
          onSuccess: () => {
            refetchRecurringData({
              currentUser,
              currentMonth,
              nextMonth,
              selectedReduxCalendar,
              selectedReduxAthlete,
              closeModal,
              calendarPickerFirstMonth,
            });
          },
          onError: () => {
            closeModal();
          },
        },
      ));
    } else if (!modalData.preSave && modalData.isRecurring) {
      const updateItem = {
        cadence: selectedCadence.id,
        position: selectedPosition.id,
        startDate: formatRangeDate(startDate),
        ...(endDate && { endDate: formatRangeDate(endDate) }),
      };

      // Add customDays if cadence is Custom (3)
      if (selectedCadence.id === 3) {
        const dayMap = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };

        updateItem.customDays = Object.entries(selectedDays)
          .filter(([, isSelected]) => isSelected)
          .map(([day]) => dayMap[day]);
      }
      /** when we update a recurring item schedule, onsuccess we need to
       * fetchCalendarPickerItems so that calendar dots update
       * we also need to re fetch recurring items so that we can get
       * the proper updates in redux. These updates for recurring are too
       * complex to try and update directly in state, so fethcing again is the
       * safest option
       */
      dispatch(updateRecurringItemSchedule(
        currentUser.accountCode,
        updateItem,
        modalData.assignedId,
        modalData.itemIndex,
        formatDate(),
        modalData.isRecurring,
        {
          onSuccess: () => {
            refetchRecurringData({
              currentUser,
              currentMonth,
              nextMonth,
              selectedReduxCalendar,
              selectedReduxAthlete,
              closeModal,
              calendarPickerFirstMonth,
            });
          },
          onError: () => {
            closeModal();
          },
        },
      ));
      closeModal();
    } else {
      closeModal();
    }
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.parentId ? 35 : 15,
    }),
    container: (provided) => ({
      ...provided,
      width: '57%',
    }),
  };

  const customStyles2 = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.parentId ? 35 : 15,
    }),
    container: (provided) => ({
      ...provided,
      width: '41%',
    }),
  };

  return (
    <ModalContainer>
      <div className='remove-div'>
        <div style={{
          display: 'flex',
          alignItems: 'center',
        }}
        >
          <TextContainer>
            <TopTextAndX>
              <ModalTextTop>Recurring Schedule</ModalTextTop>
              <RemoveDivIconWrapper
                className='remove-div-icon'
                onClick={() => closeModal()}
                style={{
                  cursor: 'pointer',
                }}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='remove'
                  color='grey'
                />
              </RemoveDivIconWrapper>
            </TopTextAndX>
            <Subtext>
              All changes made to a recurring item will be reflected on every day
              that it is scheduled
              (including past instances).
              {' '}
              <ClickHereDiv
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: '#007FAE',
                }}
                onClick={() => window.open('https://support.teambuildr.com/article/t5talti5mh-recurring-workout-items', '_blank')}
              >
                Click here to learn more.
              </ClickHereDiv>
            </Subtext>
          </TextContainer>
        </div>
      </div>
      <SchedulingOptionsStack>
        <Subtext2>
          Scheduling Options
        </Subtext2>
        <TopSelectRow>
          <Select
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            options={leftSelectOptions}
            onChange={(option) => {
              setSelectedCadence(option);
            }}
            value={selectedCadence}
            isSearchable
            styles={customStyles}
            placeholder='Select an option'
            autoFocus
          />
          <Select
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            options={rightSelectOptions}
            onChange={(option) => {
              setSelectedPosition(option);
            }}
            value={selectedPosition}
            isSearchable
            styles={customStyles2}
            placeholder='Select an option'
          />
        </TopSelectRow>
      </SchedulingOptionsStack>
      <DatePickerStack>
        <DatePickerRow>
          <PickerAndText>
            <Subtext2>
              Start Date
            </Subtext2>
            <TBDatePicker
              activeDate={startDateString}
              setActiveDate={handleStartDateChange}
              additionalStyles={{
                margin: '0',
                maxWidth: '100%',
              }}
              showDropShadow={false}
              maxDate={null}
              showIcon={false}
            />
          </PickerAndText>
          <PickerAndText>
            <Subtext2>
              End Date
            </Subtext2>
            <TBDatePicker
              activeDate={endDateString}
              setActiveDate={handleEndDateChange}
              additionalStyles={{
                margin: '0',
                maxWidth: '100%',
              }}
              showDropShadow={false}
              // minDate={startDate}
              minDate={
              startDateString
                ? new Date(
                  ...startDateString.split('-').map((v, i) => (i === 1 ? Number(v) - 1 : Number(v))),
                )
                : null
              }
              maxDate={null}
              showIcon={false}
            />
          </PickerAndText>
        </DatePickerRow>
      </DatePickerStack>
      {/* Show day selection when Custom cadence is selected */}
      {selectedCadence.id === 3 && (
        <div>
          <SelectDaysLabel>Select Days</SelectDaysLabel>
          <DaySelectors>
            <DaySelector
              selected={selectedDays.sunday}
              onClick={() => toggleDay('sunday')}
            >
              Su
            </DaySelector>
            <DaySelector
              selected={selectedDays.monday}
              onClick={() => toggleDay('monday')}
            >
              M
            </DaySelector>
            <DaySelector
              selected={selectedDays.tuesday}
              onClick={() => toggleDay('tuesday')}
            >
              T
            </DaySelector>
            <DaySelector
              selected={selectedDays.wednesday}
              onClick={() => toggleDay('wednesday')}
            >
              W
            </DaySelector>
            <DaySelector
              selected={selectedDays.thursday}
              onClick={() => toggleDay('thursday')}
            >
              Th
            </DaySelector>
            <DaySelector
              selected={selectedDays.friday}
              onClick={() => toggleDay('friday')}
            >
              F
            </DaySelector>
            <DaySelector
              selected={selectedDays.saturday}
              onClick={() => toggleDay('saturday')}
            >
              Sa
            </DaySelector>
          </DaySelectors>
        </div>
      )}
      <ButtonsContainer>
        <NewCalendarButton onClick={() => {
          handleSave();
        }}
        >
          SAVE
        </NewCalendarButton>
        <ManageCalendarsButton onClick={() => closeModal()}>
          CANCEL
        </ManageCalendarsButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

SetRecurringItemModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default SetRecurringItemModal;
